import styled from '@emotion/styled';
import { ButtonBase, CircularProgress, Typography } from '@material-ui/core';
import { AlertCircle, File } from 'lucide-react';
import { Img } from 'react-image';
import { useDialogState } from '../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../i18next';
import { formatBytes } from '../../services/bytes';
import { useTheme } from '../../themes';
import {
  PdfDialog,
  PdfDialogState,
  PDF_VIEWER_DEFAULT_KEYMAP
} from '../PdfDialog';

const ClickableContainer = styled(ButtonBase)((p) => ({
  '&&': {
    border: p.theme.custom.border.standard,
    borderRadius: p.theme.custom.border.radius(),
    display: 'grid',
    gridTemplateColumns: 'min-content 1fr',
    gap: p.theme.spacing(2),
    padding: p.theme.spacing(2),
    width: '100%',
    cursor: 'pointer',
    textAlign: 'center',

    '> img': {
      maxHeight: 120,
      maxWidth: 200,
      objectFit: 'cover'
    },

    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
  
      '> img': {
        maxHeight: '50vh',
        maxWidth: '100%',
      },
    }
  }
}));

const Container = styled('div')((p) => ({
  position: 'relative',
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  display: 'grid',
  gap: p.theme.spacing(2),
  padding: p.theme.spacing(2),
  width: '100%',
  textAlign: 'center',

  '> img': {
    maxHeight: 120,
    maxWidth: 200,
    objectFit: 'cover'
  },

  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',

    '> img': {
      maxHeight: 'unset',
      maxWidth: 'unset',
    },
  }
}));

const Overlay = styled('div')((p) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgb(0 0 0 / 65%)',
  pointerEvents: 'none',
  borderRadius: p.theme.custom.border.radius()
}));

const EmptyBlock = styled('div')<{ height?: number; width?: number }>((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  height: p.height || undefined,
  width: p.width || undefined,
  color: p.theme.custom.colors.gray.w400
}));

export const DocumentTeaser = ({
  title,
  contentType,
  pages,
  sizeInBytes,
  docUrl,
  thumbnailUrl,
  overlay,
  onClick,
  actions
}: {
  title: string;
  contentType: 'application/pdf';
  pages: number;
  sizeInBytes: number;
  thumbnailUrl: string;
  docUrl: string;
  overlay?: React.ReactNode;
  onClick?: () => void;
  actions?: (props: {
    state: PdfDialogState;
    close: () => void;
  }) => React.ReactNode;
}) => {
  const { t } =  useUnsafeTranslation();
  const theme = useTheme();
  const ContainerComp = onClick ? ClickableContainer : Container;
  return (
    <ContainerComp onClick={onClick}>
      {!docUrl && (
        <EmptyBlock width={200} height={120}>
          <File size={20} />
        </EmptyBlock>
      )}
      {docUrl && (
        <Img
          alt={title}
          title={title}
          src={thumbnailUrl}
          key={thumbnailUrl}
          crossorigin="anonymous"
          unloader={
            <EmptyBlock width={200} height={120}>
              <AlertCircle size={20} />
              <div>{t('Document not found')}</div>
            </EmptyBlock>
          }
          loader={
            <EmptyBlock width={200} height={120}>
              <CircularProgress color="inherit" />
            </EmptyBlock>
          }
          style={{ border: `1px solid ${theme.custom.colors.gray.w200}` }}
        />
      )}
      <div>
        <Typography
          component="div"
          variant="h5"
          color={title ? 'textPrimary' : 'textSecondary'}
          align="left"
        >
          {title ? title : t('Untitled document')}
        </Typography>
        <Typography component="div" variant="caption" align="left">
          {t('PDF Document')} • {t('{{ count }} pages', { count: pages })}
        </Typography>

        <Typography component="div" variant="caption" align="left">
          {formatBytes(sizeInBytes)}
        </Typography>
      </div>
      {overlay && <Overlay>{overlay}</Overlay>}
    </ContainerComp>
  );
};

export const DocumentTeaserWithPreview = ({
  autoOpen,
  pdfActions,
  ...props
}: {
  title: string;
  contentType: 'application/pdf';
  pages: number;
  sizeInBytes: number;
  thumbnailUrl: string;
  docUrl: string;
  autoOpen?: boolean;
  overlay?: React.ReactNode;
  pdfActions?: (props: {
    state: PdfDialogState;
    close: () => void;
  }) => React.ReactNode;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(!!autoOpen);

  return (
    <>
      <DocumentTeaser {...props} onClick={openDialog} />
      {dialogOpen && (
        <PdfDialog
          open={true}
          onClose={() => closeDialog()}
          fullScreen
          title={props.title}
          file={{
            url: props.docUrl
          }}
          keyMap={PDF_VIEWER_DEFAULT_KEYMAP}
          actions={pdfActions}
        />
      )}
    </>
  );
};
